<template>
    <Dialog :visible="showDialog" class="action-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header-danger">
                Transfer Ownership
            </div>
            <div class="dialog-content align-items-center">
                <div class="w-full flex justify-content-center align-items-center mb-3">
                    <SvgWarning className="popup-warning-svg" color="var(--popup-warning-color)"/>
                </div>
                <span class="text mb-3 px-1 flex flex-column align-items-center text-center gap-1">
                    <span>
                        You are about to transfer the ownership of the license to
                        <span style="font-weight: var(--font-weight-normal)">{{ `${newOwner.first_name ?? "?"} ${newOwner.last_name ?? "?"} (${newOwner.email ?? "?"})` }}</span>.
                    </span>
                    <span>
                        By giving the ownership, you decide to set
                        <span style="font-weight: var(--font-weight-normal)">this user as the new owner</span>
                        of the license. Therefore, you will
                        <span style="font-weight: var(--font-weight-normal); color: var(--mydanger-color)">lose your owner status and rights</span>
                        for this license and belong to its 'Authorized Users'.
                    </span>
                </span>
                
                <div class="content flex flex-column w-full justify-content-center align-items-center mt-2">
                    <DataTable :value="[license]" size="small" tableStyle="min-width: 100%" :row-class="() => 'row'" class="mb-2" scrollable scrollHeight="flex" style="max-height: 25vh; width: 95%;">
                        <Column field="serial_number" header="Serial number">
                            <template #body="slotProps">
                                <span class="text-serial-number">{{slotProps.data.serial_number}}</span>
                            </template>
                        </Column>
                        <Column field="product.application" header="Application" style="max-width: 30%"></Column>
                        <Column field="product.exp" header="Expiration">
                            <template #body="slotProps">
                                <span v-if="slotProps.data.product.exp === '-1'" class="font-italic">None</span>
                                <span v-else>{{format(new Date(slotProps.data.product.exp), 'MMM do, yyyy')}}</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <div class="mt-2 flex flex-column text">
                    <span class="text-center px-2 sm:px-7">Are you sure you want to transfer ownership for this license to that user ?</span>
                </div>
            </div>

            <div class="dialog-footer">
                <a  class="justify-self-start flex align-items-center justify-content-center" :href="transferOwnershipZendeskLink" target="_blank" @click="clickedZendeskLink">
                    <Button rounded text style="border-radius: 980px; padding: 0;">
                        <SvgQuestionMarkCircle className="info-svg" color="var(--button-normal-color)"/>
                    </Button>
                </a>
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="$emit('close')"/>
                    <ButtonModule type="primary" severity="danger" label="Transfer" @click="showPasswordVerificationDialog = true"/>
                </div>
            </div>
        </template>
    </Dialog>

    <!-- DIALOGS -->

    <!-- Authentication using PASSWORD -->
    <VerificationAuthenticationDialog :showDialog="showPasswordVerificationDialog" :incorrectPassword="wrongPassword" @cancel="showPasswordVerificationDialog = false;" @ok="(val) => verificationAuthenticationDone(val)" :isLoading="httpTransferOwnershipLoading"/>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed, watchEffect } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
// vue
import ButtonModule from '@/components/Modules/ButtonModule.vue'
import VerificationAuthenticationDialog from '@/components/Dialogs/OtherDialogs/VerificationAuthenticationDialog.vue';
import SvgWarning from '@/components/SVGs/SvgWarning.vue';
import SvgQuestionMarkCircle from '@/components/SVGs/SvgQuestionMarkCircle.vue';
// Others
import toastService from '@/composables/toastService';
import UserUtils from '@/composables/UserUtils';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'
import { format } from 'date-fns';

const store = useStore()
const router = useRouter()
const { logoutUser } = UserUtils(store, router)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchTransferOwnership } = useApiRequest()
const httpTransferOwnershipLoading = ref(false)
const httpTransferOwnershipData = ref(null)

// --- isWebView info ---
const isWebView = computed(() => store.state.applicationWebView.isWebView)
const applicationGlobalInfo = computed(() => store.state.applicationWebView.applicationGlobalInfo)

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    license: Object,
    newOwner: Object
})
const { showDialog, license, newOwner } = toRefs(props)

// --- EMITS ---
const emit = defineEmits(['close', 'close-license-window', 'refresh-license', 'refresh-licenses-table'])

// --- PASSWORD VERIFICATION ---
const showPasswordVerificationDialog = ref(false)
const wrongPassword = ref(false)
// Whenever the dialog opens, it should reset the wrongPassword boolean
watchEffect(() => {
    if (showPasswordVerificationDialog.value) { wrongPassword.value = false }
})
const verificationAuthenticationDone = (password) => {
    // IT CALLS THE SUBMIT REQUEST WITH THE PASSWORD AS THE VERIFICATION
   submitTransferOwnershipConfirmed(password)
}

// --- SUBMIT TRANSFER OWNERSHIP CONFIRMED ---
const submitTransferOwnershipConfirmed = async (passwordVerification) => {
    wrongPassword.value = false
    // Send HTTP request
    httpTransferOwnershipLoading.value = true
    try {
        httpTransferOwnershipData.value = await fetchTransferOwnership({ password_plain_auth: passwordVerification, serial_numbers: [license.value.serial_number], new_owner_id: newOwner.value.id })
        // Reset of values
        showPasswordVerificationDialog.value = false
        wrongPassword.value = false
        // Refresh of data
        if (httpTransferOwnershipData.value.licenses.length < 1) {
            toastService.add({ severity: 'error', summary: `Transfer Ownership: Unexpected issue.`, detail: `Please refresh the page and try again. If the issue persists, contact us.`, life: 8000 });
        } else {
            store.dispatch('licenses/updateLicense', httpTransferOwnershipData.value.licenses[0])
            emit("refresh-license")
            emit("close")
            license.value = httpTransferOwnershipData.value.licenses[0]
        }
    } catch (err) {
        handleTransferOwnershipError(err)
    } finally {
        httpTransferOwnershipLoading.value = false
    }
}
// Handle an error from the server
const handleTransferOwnershipError = (error) => {
    try {  
      if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
          toastService.add({ severity: 'error', summary: `Transfer Ownership: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
          return;
      }
      const errorStatus = error.response.status
      const errorCode = error.response.data.error_code
      const errorMessage = error.response.data.error_message
      switch (errorStatus) {
        case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM / INPUTS_INVALID (400)
            if (errorCode == 'ACCESS_TOKEN_MISSING') {
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                // Logout
                logoutUser()
            }
            else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                toastService.add({ severity: 'warn', summary: "Transfer Ownership: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / WRONG_PASSWORD (401)
            if (errorCode == 'WRONG_PASSWORD') { // WRONG_PASSWORD
                wrongPassword.value = true
            }    
            else if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                // Logout: Do nothing, the interceptor should have made a refresh token request!
                // logoutUser()
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 404: // SERIAL_INVALID / ACCOUNT_NOT_FOUND / NEW_OWNER_ACCOUNT_NOT_FOUND (404) => toast + logout
            if (errorCode == 'SERIAL_INVALID') { // SERIAL_INVALID
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Invalid Serial Number.", detail: 'The license is invalid and has been deleted from the table.', life: 5000 });
                store.commit('licenses/removeLicense', license.value.serial_number)
                emit('refresh-licenses-table')
                emit('close-license-window')
            }
            else if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                // Logout
                logoutUser()
            }
            else if (errorCode == 'NEW_OWNER_ACCOUNT_NOT_FOUND') { // NEW_OWNER_ACCOUNT_NOT_FOUND
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Other User Not Found.", detail: 'The account of the user you want to transfer to does not exist. Please refresh and try again.', life: 8000 });
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 409: // USER_NOT_OWNER (409)
            if (errorCode == 'USER_NOT_OWNER') { // USER_NOT_OWNER
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Not Authorized.", detail: errorMessage, life: 5000 });
                emit('refresh-licenses-table')
                emit('close-license-window')
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
            if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                toastService.add({ severity: 'error', summary: "Transfer Ownership: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
            }
            else { toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
            break;
        default:
            toastService.add({ severity: 'error', summary: `Transfer Ownership: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
            break;
      }
    } catch {
        toastService.add({ severity: 'error', summary: `Transfer Ownership: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- Support link ---
const transferOwnershipZendeskLink = "https://softron.zendesk.com/hc/en-us/sections/14666349935516-Softron-License-Manager-New-2024-Activations"
const clickedZendeskLink = () => {
    // (in case it is in the web view in the app, ask the app to open it also, because the href thing will not work if it is in the web view)
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
        window.webkit.messageHandlers.jsOpenWebPage.postMessage(transferOwnershipZendeskLink);
    }
}
</script>

<style scoped>
</style>